import api from '@/lib/api'
import type { Notification } from '@/types'
import type { APIErrorResponse } from '@/types/errors'
import { extractAPIError } from '@/lib/error-utils'

/**
 * Typed result wrapper for service operations.
 * Enables discriminated union pattern for error handling.
 */
export type Result<T, E = APIErrorResponse> =
  | { success: true; data: T }
  | { success: false; error: E }

interface NotificationListResponse {
  notifications: Notification[]
  total: number
  unread_count: number
}

export const notificationsService = {
  /**
   * List all notifications for the current user.
   */
  async list(): Promise<Result<NotificationListResponse>> {
    try {
      const { data } = await api.get<NotificationListResponse>('/notifications')
      return { success: true, data }
    } catch (error) {
      const apiError = extractAPIError(error)
      return { success: false, error: apiError || { error_code: 'UNKNOWN_ERROR', message: String(error) } }
    }
  },

  /**
   * Mark a notification as read.
   */
  async markAsRead(id: string): Promise<Result<void>> {
    try {
      await api.patch(`/notifications/${id}/read`)
      return { success: true, data: undefined }
    } catch (error) {
      const apiError = extractAPIError(error)
      return { success: false, error: apiError || { error_code: 'UNKNOWN_ERROR', message: String(error) } }
    }
  },

  /**
   * Mark all notifications as read.
   */
  async markAllAsRead(): Promise<Result<void>> {
    try {
      await api.post('/notifications/mark-all-read')
      return { success: true, data: undefined }
    } catch (error) {
      const apiError = extractAPIError(error)
      return { success: false, error: apiError || { error_code: 'UNKNOWN_ERROR', message: String(error) } }
    }
  },
}
